/**
 * Generación de etiquetas ZPL para impresoras Zebra (ej. Zebra 220).
 * Tamaño configurable en mm + DPI. Contenido: nombre, precio y peso/unidad.
 */

export interface LabelConfig {
  widthMm: number;    // ancho de la etiqueta en mm
  heightMm: number;   // alto de la etiqueta en mm
  dpi: number;        // 203 (8 dpmm) o 300 (12 dpmm) típicos
  copies: number;     // copias por producto
  showName: boolean;
  showPrice: boolean;
  showWeight: boolean;
  showExpiration: boolean;   // mostrar fecha de vencimiento (si el producto la tiene)
  showDivider: boolean;      // línea divisoria antes del precio
  showBarcode: boolean;      // código de barras (con el código del producto)
  marginMm: number;   // margen interno
  // Escala del tamaño de letra por línea (1 = automático). Menor = más chico.
  nameScale: number;
  priceScale: number;
  weightScale: number;
  expirationScale: number;
  barcodeScale: number;
  // Alineación (L=izquierda, C=centro, R=derecha)
  expirationAlign: 'L' | 'C' | 'R';
  barcodeAlign: 'L' | 'C' | 'R';
  // Tipografía ZPL: '0' (Triumvirate escalable, default) o bitmap 'A'..'H'
  font: string;
}

export const DEFAULT_LABEL_CONFIG: LabelConfig = {
  widthMm: 50,
  heightMm: 25,
  dpi: 203,
  copies: 1,
  showName: true,
  showPrice: true,
  showWeight: true,
  showExpiration: false,
  showDivider: true,
  showBarcode: false,
  marginMm: 2,
  nameScale: 1,
  priceScale: 1,
  weightScale: 1,
  expirationScale: 1,
  barcodeScale: 1,
  expirationAlign: 'C',
  barcodeAlign: 'C',
  font: '0',
};

export interface LabelData {
  name: string;
  priceText?: string;      // ej "$2.500" o "$2.500/kg"
  weightText?: string;     // ej "500 gr" o "1 u"
  expirationText?: string; // ej "04/2027" (mm/yyyy)
  barcodeText?: string;    // código para el código de barras (ej el código del producto)
}

const mmToDots = (mm: number, dpi: number) => Math.round((mm * dpi) / 25.4);

// Ruta donde se guarda la fuente TrueType personalizada en la impresora (font '@').
export const FONT_TTF_PATH = 'E:MSJHENGH.TTF';

/**
 * Arma el comando ZPL ~DY para descargar una fuente TrueType (.ttf) a la memoria
 * de la impresora, así se puede imprimir con ella (^A@). Se envía UNA vez.
 * bytes = contenido del .ttf; se manda en hexadecimal (formato 'A').
 */
export function buildFontDownloadZpl(bytes: Uint8Array): string {
  let hex = '';
  for (let i = 0; i < bytes.length; i++) hex += bytes[i].toString(16).padStart(2, '0').toUpperCase();
  // ~DYd:f,b,x,t,w,data  → d:f=E:MSJHENGH, b=A (ASCII hex), x=TTF, t=total bytes
  return `~DYE:MSJHENGH,A,TTF,${bytes.length},,${hex}`;
}

// Escapa caracteres especiales de ZPL (^ y ~) en el texto del usuario
const esc = (s: string) => (s || '').replace(/[\^~]/g, ' ').trim();

/**
 * Genera el ZPL de UNA etiqueta (un ^XA ... ^XZ). Incluye ^PQ para las copias.
 */
export function buildLabelZpl(data: LabelData, cfg: LabelConfig): string {
  const W = mmToDots(cfg.widthMm, cfg.dpi);
  const H = mmToDots(cfg.heightMm, cfg.dpi);
  const M = mmToDots(cfg.marginMm, cfg.dpi);
  const innerW = Math.max(10, W - M * 2);
  const F = cfg.font || '0'; // tipografía ZPL

  // Comando de fuente por campo. Si es '@' usamos una TrueType cargada en la
  // impresora (ej. Microsoft JhengHei UI subida a E:MSJHENGH.TTF); si es una
  // fuente interna (0/A..H) usamos ^A<F>N. Se aplica por campo (antes del ^FB).
  const fontAt = (size: number) =>
    F === '@' ? `^A@N,${size},${size},${FONT_TTF_PATH}` : `^A${F}N,${size},${size}`;

  // Tamaños de fuente proporcionales al alto de la etiqueta, ajustables por escala.
  const nameFont = Math.max(12, Math.round(H * 0.15 * (cfg.nameScale ?? 1)));
  const priceFont = Math.max(16, Math.round(H * 0.30 * (cfg.priceScale ?? 1)));
  const weightFont = Math.max(11, Math.round(H * 0.12 * (cfg.weightScale ?? 1)));
  const expFont = Math.max(9, Math.round(H * 0.09 * (cfg.expirationScale ?? 1)));
  const dividerT = Math.max(2, Math.round(H * 0.015));

  // Estimación de líneas del nombre (para no reservar 2 líneas cuando entra en 1).
  const approxCharW = nameFont * 0.55;
  const perLine = Math.max(1, Math.floor(innerW / Math.max(1, approxCharW)));
  const nameLines = cfg.showName && data.name && data.name.length > perLine ? 2 : 1;

  // Cada bloque: su alto y cómo se dibuja a partir de una y.
  type Block = { h: number; draw: (y: number) => void };
  const blocks: Block[] = [];
  const out: string[] = [];

  if (cfg.showName && data.name) {
    blocks.push({
      h: nameFont * nameLines,
      draw: (y) => {
        out.push(`^FO${M},${y}${fontAt(nameFont)}^FB${innerW},${nameLines},0,C,0^FD${esc(data.name)}^FS`);
      },
    });
  }
  if (cfg.showWeight && data.weightText) {
    blocks.push({
      h: weightFont,
      draw: (y) => {
        out.push(`^FO${M},${y}${fontAt(weightFont)}^FB${innerW},1,0,C,0^FD${esc(data.weightText!)}^FS`);
      },
    });
  }
  if (cfg.showDivider && (cfg.showName || cfg.showWeight)) {
    blocks.push({
      h: dividerT,
      draw: (y) => out.push(`^FO${M},${y}^GB${innerW},${dividerT},${dividerT}^FS`),
    });
  }
  if (cfg.showPrice && data.priceText) {
    blocks.push({
      h: priceFont,
      draw: (y) => {
        out.push(`^FO${M},${y}${fontAt(priceFont)}^FB${innerW},1,0,C,0^FD${esc(data.priceText!)}^FS`);
      },
    });
  }
  if (cfg.showBarcode && data.barcodeText) {
    const bcH = Math.max(16, Math.round(H * 0.26 * (cfg.barcodeScale ?? 1)));
    const digitsH = Math.round(bcH * 0.4); // línea de dígitos que imprime ^BC
    const mod = 2;
    const estW = Math.min(innerW, (11 * String(data.barcodeText).length + 35) * mod);
    let bx = M;
    if (cfg.barcodeAlign === 'R') bx = Math.max(M, W - M - estW);
    else if (cfg.barcodeAlign === 'C') bx = Math.max(M, Math.round((W - estW) / 2));
    blocks.push({
      h: bcH + digitsH,
      draw: (y) => {
        out.push(`^BY${mod},2,0`);
        out.push(`^FO${bx},${y}^BCN,${bcH},Y,N,N^FD${esc(data.barcodeText!)}^FS`);
      },
    });
  }
  if (cfg.showExpiration && data.expirationText) {
    blocks.push({
      h: expFont,
      draw: (y) => {
        out.push(`^FO${M},${y}${fontAt(expFont)}^FB${innerW},1,0,${cfg.expirationAlign || 'C'},0^FD${esc('Venc ' + data.expirationText)}^FS`);
      },
    });
  }

  // Gap uniforme entre bloques. Se centra todo el conjunto verticalmente para
  // que no queden huecos disparejos ni espacio muerto arriba/abajo.
  const gap = Math.max(2, Math.round(H * 0.045));
  const contentH = blocks.reduce((s, b) => s + b.h, 0) + gap * Math.max(0, blocks.length - 1);
  let y = Math.max(M, Math.round((H - contentH) / 2));

  const lines: string[] = ['^XA', `^PW${W}`, `^LL${H}`, '^LH0,0', '^CI28'];
  blocks.forEach((b, i) => {
    b.draw(y);
    y += b.h + (i < blocks.length - 1 ? gap : 0);
  });
  lines.push(...out);
  lines.push(`^PQ${Math.max(1, cfg.copies)}`);
  lines.push('^XZ');
  return lines.join('\n');
}

/**
 * Genera el ZPL de un lote de etiquetas (varias etiquetas en un solo envío).
 */
export function buildBatchZpl(items: LabelData[], cfg: LabelConfig): string {
  return items.map((it) => buildLabelZpl(it, cfg)).join('\n');
}
